import { useState, useMemo, useEffect } from "react";
import clsx from "clsx";

// MUI
import { Dialog, DialogTitle, DialogActions, IconButton, Divider, Stack } from "@mui/material";
import { styled } from "@mui/material/styles";

// Components
import Button from "@/Layouts/components/Buttons/Button";

// Icons
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

// Interfaces
import { IPayPeriod } from "@/Common/api/types/CommissionCycle";

// Helpers
import { PositionDialogContent } from "./PositionDialogContent";
import { TabsSplitPayPeriod } from "./TabsSplitPayPeriod";
import { Nullable } from "@/Common/interfaces/types";
import { IIsConfirmDisabledBtn, IPositionsDialogProps } from "@/Washouts/types/PositionsDialog";
import { and, or } from "@/Common/helpers/sonar";

const BootstrapDialog = styled(Dialog)(() => ({
	"& .MuiPaper-root": {
		borderRadius: "10px",
	},
}));

const getPositions = (payPeriod: Nullable<IPayPeriod>, disabledPositions: string[]) => {
	if (!payPeriod?.positionStatus?.length) {
		return [];
	}
	return payPeriod.positionStatus?.map((ppItem) => {
		return {
			name: ppItem.position,
			disabled: disabledPositions.includes(ppItem.position),
		};
	});
};
const isConfirmDisabledBtn = (args: IIsConfirmDisabledBtn) => {
	const { hasPartnerPeriod, selectedPositions, selectedPartnerPositions, isPendingMutation } = args;
	if (!hasPartnerPeriod) {
		return selectedPositions.length === 0 || isPendingMutation;
	}

	return (selectedPositions.length === 0 && selectedPartnerPositions.length === 0) || isPendingMutation;
};

export default function PositionsDialog(props: Readonly<IPositionsDialogProps>) {
	const {
		open,
		action,
		isPendingMutation = false,
		currentPayPeriod,
		partnerPayPeriod,
		layoutDialogProps = { title: "Approve Positions", btnConfirmText: "Approve", btnCancelText: "Cancel" },
		isUsingCallback = false,
		setOpenPositionsSelector,
	} = props;

	const { payPeriod, commissionCycleId, disabledPositions = [], shouldDisabled = false } = currentPayPeriod ?? {};

	const {
		payPeriod: partnerPayPeriodValue,
		commissionCycleId: partnerCommissionCycleId,
		disabledPositions: partnerDisabledPositions = [],
		shouldDisabled: shouldDisabledPartner = false,
	} = partnerPayPeriod ?? {};

	const hasPartnerPeriod = !!partnerPayPeriodValue;

	const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
	const [selectedPartnerPositions, setSelectedPartnerPositions] = useState<string[]>([]);
	const [selectedTabs, setSelectedTabs] = useState<number>(0);

	const sortOrder = useMemo(() => {
		const currentDate = new Date(payPeriod?.startDate ?? 0).getTime();
		const partnerDate = new Date(partnerPayPeriodValue?.startDate ?? 0).getTime();
		return currentDate <= partnerDate ? [0, 1] : [1, 0];
	}, [payPeriod?.startDate, partnerPayPeriodValue?.startDate]);

	const currentPayPeriodPosition = useMemo(() => {
		return getPositions(payPeriod, disabledPositions);
	}, [payPeriod, disabledPositions]);

	const partnerPayPeriodPosition = useMemo(() => {
		return getPositions(partnerPayPeriodValue, partnerDisabledPositions);
	}, [partnerPayPeriodValue, partnerDisabledPositions]);

	const enabledPositions = useMemo(() => currentPayPeriodPosition.filter((p) => !p.disabled), [currentPayPeriodPosition]);
	const partnerEnabledPositions = useMemo(() => partnerPayPeriodPosition.filter((p) => !p.disabled), [partnerPayPeriodPosition]);

	const checkedAll = useMemo(
		() => enabledPositions.length > 0 && selectedPositions.length === enabledPositions.length,
		[selectedPositions, enabledPositions]
	);

	const checkedAllPartner = useMemo(
		() => partnerEnabledPositions.length > 0 && selectedPartnerPositions.length === partnerEnabledPositions.length,
		[selectedPartnerPositions, partnerEnabledPositions]
	);

	const getCheckAllHandler = (logicalIndex: number) => (checked: boolean) => {
		if (logicalIndex === 1) {
			setSelectedPartnerPositions(checked ? partnerEnabledPositions.map((p) => p.name) : []);
		} else {
			setSelectedPositions(checked ? enabledPositions.map((p) => p.name) : []);
		}
	};

	const togglePosition = (prev: string[], positionName: string) =>
		prev.includes(positionName) ? prev.filter((name) => name !== positionName) : [...prev, positionName];

	const getToggleHandler = (logicalIndex: number) => (positionName: string) => {
		if (!positionName) {
			return;
		}
		const setter = logicalIndex === 1 ? setSelectedPartnerPositions : setSelectedPositions;
		setter((prev) => togglePosition(prev, positionName));
	};

	const handleConfirm = () => {
		if (and(!selectedPositions?.length, !selectedPartnerPositions?.length)) {
			return;
		}

		if (isUsingCallback) {
			action({
				selectedPositionStatus: selectedPositions,
				selectedPartnerPositionStatus: selectedPartnerPositions,
			});
		} else {
			if (selectedPositions?.length) {
				action({ commissionCycleId: commissionCycleId || "", payPeriodId: payPeriod?._id ?? "", positions: selectedPositions });
			}

			if (and(hasPartnerPeriod, Boolean(selectedPartnerPositions?.length))) {
				action({
					commissionCycleId: partnerCommissionCycleId || "",
					payPeriodId: partnerPayPeriodValue?._id ?? "",
					positions: selectedPartnerPositions,
				});
			}
		}
		handleClose();
	};

	const handleClose = () => {
		setSelectedPositions([]);
		setSelectedPartnerPositions([]);
		setSelectedTabs(0);
		setOpenPositionsSelector(false);
	};

	const isConfirmDisabled = isConfirmDisabledBtn({
		hasPartnerPeriod,
		selectedPositions,
		selectedPartnerPositions,
		isPendingMutation,
	});

	useEffect(() => {
		const setEnabledTabIndex = sortOrder.findIndex((logicalIdx) => {
			const disabled = or(
				(logicalIdx === 0 ? currentPayPeriodPosition : partnerPayPeriodPosition).length === 0,
				logicalIdx === 0 ? shouldDisabled : shouldDisabledPartner
			);
			return !disabled;
		});
		if (setEnabledTabIndex !== -1) {
			setSelectedTabs(setEnabledTabIndex);
		}
	}, [sortOrder, currentPayPeriodPosition, partnerPayPeriodPosition]);

	const tabs = sortOrder.map((logicalIdx) => {
		const disabled = or(
			(logicalIdx === 0 ? currentPayPeriodPosition : partnerPayPeriodPosition).length === 0,
			logicalIdx === 0 ? shouldDisabled : shouldDisabledPartner
		);
		return {
			disabled,
			title: logicalIdx === 0 ? payPeriod?.title : partnerPayPeriodValue?.title,
		};
	});

	const badgeValues: Record<number, number> = {
		0: sortOrder[0] === 0 ? selectedPositions.length : selectedPartnerPositions.length,
		1: sortOrder[1] === 0 ? selectedPositions.length : selectedPartnerPositions.length,
	};

	const contentMap = sortOrder.map((logicalIdx) => {
		const isPartner = logicalIdx === 1;
		return (
			<PositionDialogContent
				key={isPartner ? `${partnerCommissionCycleId}_${partnerPayPeriodValue?._id}` : `${commissionCycleId}_${payPeriod?._id}`}
				sx={{ marginTop: "24px", marginLeft: "-25px" }}
				payPeriodPosition={isPartner ? partnerPayPeriodPosition : currentPayPeriodPosition}
				isCheckedAll={isPartner ? checkedAllPartner : checkedAll}
				handleCheckedAll={getCheckAllHandler(logicalIdx)}
				selectedPositions={isPartner ? selectedPartnerPositions : selectedPositions}
				handleTogglePosition={getToggleHandler(logicalIdx)}
			/>
		);
	});

	return (
		<BootstrapDialog open={open} onClose={handleClose} disableEscapeKeyDown>
			<DialogTitle marginTop={1} marginLeft={1} marginBottom={hasPartnerPeriod ? 2 : 0}>
				{layoutDialogProps.title}
				<IconButton
					aria-label="close"
					onClick={handleClose}
					sx={{
						position: "absolute",
						right: 5,
						top: 5,
						color: (theme) => theme.palette.text.secondary,
					}}
				>
					<CloseRoundedIcon />
				</IconButton>
			</DialogTitle>
			{hasPartnerPeriod && (
				<>
					<TabsSplitPayPeriod
						badgeValues={badgeValues}
						tabs={tabs}
						tabSelected={selectedTabs}
						setTabSelected={setSelectedTabs}
						memoizedContentMap={contentMap}
					/>
					<Divider
						sx={{
							minWidth: 456,
							maxHeight: 0,
							marginTop: 4,
							marginLeft: 4,
							marginRight: 4,
							border: "1px solid var(--General-General-Stroke, #CCCCCC)",
						}}
					/>
				</>
			)}
			{!hasPartnerPeriod && (
				<PositionDialogContent
					payPeriodPosition={currentPayPeriodPosition}
					isCheckedAll={checkedAll}
					handleCheckedAll={getCheckAllHandler(0)}
					selectedPositions={selectedPositions}
					handleTogglePosition={getToggleHandler(0)}
				/>
			)}

			<DialogActions sx={{ marginTop: 3, marginBottom: 4, px: "32px", justifyContent: "flex-end" }}>
				<Stack direction="row" spacing={3}>
					<Button
						variant="contained"
						classes="!bg-gray-blue h-[30px] rounded-[6px] !shadow-none hover:!shadow-none !font-sans !capitalize !text-white !text-[14px]"
						text={layoutDialogProps.btnCancelText ?? "Cancel"}
						sx={{ minWidth: "82px", width: "100px", minHeight: "30px", height: "30px" }}
						onClick={handleClose}
					/>
					<Button
						variant="contained"
						classes={clsx({
							"h-[30px] rounded-[6px] !shadow-none !font-sans !capitalize !text-white !text-[14px]": true,
							"!bg-primary hover:!shadow-primary-shadow-50": !isConfirmDisabled,
							"!bg-disabled !cursor-default !shadow-none hover:!shadow-none": isConfirmDisabled,
						})}
						sx={{ minWidth: "82px", width: "100px", height: "30px", minHeight: "30px" }}
						text={layoutDialogProps.btnConfirmText}
						disabled={isConfirmDisabled}
						onClick={handleConfirm}
					/>
				</Stack>
			</DialogActions>
		</BootstrapDialog>
	);
}
